/**
 * One topic of a notification setup section: one Bell and one Email box for the
 * events it groups, opening to those events for anyone who wants to customise.
 *
 * Each box changes the rows it is handed, which under a search means the rows
 * shown and nothing past them. Required and retired rows are left alone, and a
 * box that reads a mix of rows shows a dash until it is clicked.
 *
 * @link ./eventGroups for what makes a topic.
 * @link ./eventState for what each box reads and writes.
 */
import type {
  NotificationChannel,
  NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Checkbox } from '../../../ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../ui/collapsible';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { TooltipProvider } from '../../../ui/tooltip';
import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import { NotificationEventRow } from './NotificationEventRow';
import type { EventTopic } from './eventGroups';
import { REACH_HINTS, REACH_LABELS, type EventReach } from './eventReach';
import {
  canTurnOn,
  isEventOn,
  topicChannelState,
  topicControls,
  type TopicChannel,
} from './eventState';
import {
  CHANNEL_LABELS,
  ONE_DAILY_SENTENCE,
  SETUP_CHANNELS,
  type EventConfig,
  type OneDailyDelivery,
  type SetupChoice,
  type SetupEvent,
} from './types';

/** The topic list's columns, shared by its header and every row. */
export const TOPIC_GRID = 'grid grid-cols-[minmax(0,1fr)_88px_56px_56px_88px_116px] items-center gap-2 px-4';

interface NotificationTopicRowProps {
  /** The rows the section is showing for this topic: all it may change. */
  topic: EventTopic;
  /** How many rows the topic holds before a search narrows it. */
  total: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canConfigure: boolean;
  frequencyChoices: SetupChoice[];
  defaultFrequency: NotificationTenantDefaultFrequency;
  emailsEnabled: boolean;
  /** Tenant Bell master switch: with it off, nothing is grouped. */
  bellEnabled: boolean;
  digestEnabled: boolean;
  digestDelivery: OneDailyDelivery;
  configFor: (event: SetupEvent) => EventConfig;
  isDirty: (event: SetupEvent) => boolean;
  onUpdate: (event: SetupEvent, patch: Partial<EventConfig>) => void;
  onToggleChannel: (event: SetupEvent, channel: NotificationChannel, on: boolean) => void;
  onReset: (event: SetupEvent) => void;
  onSetTopicChannel: (events: SetupEvent[], channel: TopicChannel, on: boolean) => void;
}

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

export function NotificationTopicRow({
  topic,
  total,
  open,
  onOpenChange,
  canConfigure,
  frequencyChoices,
  defaultFrequency,
  emailsEnabled,
  bellEnabled,
  digestEnabled,
  digestDelivery,
  configFor,
  isDirty,
  onUpdate,
  onToggleChannel,
  onReset,
  onSetTopicChannel,
}: NotificationTopicRowProps) {
  const { events, label } = topic;
  const live = events.filter(canTurnOn);
  const onCount = live.filter((event) =>
    isEventOn(event, configFor(event), defaultFrequency),
  ).length;
  const required = live.filter((event) => !event.canDisable).length;
  const unsaved = events.filter(isDirty).length;
  const customised = events.filter((event) => event.isOverridden && !isDirty(event)).length;
  const narrowed = events.length !== total;
  const sides = new Set(events.map((event) => event.reaches));
  const reach: EventReach = sides.size === 1 ? events[0]?.reaches ?? 'both' : 'both';

  const box = (channel: TopicChannel, name: string, hint?: string) => {
    const state = topicChannelState(events, configFor, channel, defaultFrequency);
    // Rows at a frequency of Off the box cannot wake: it reads the others.
    const silent = channel === 'mail'
      ? live.filter((event) =>
        event.canDisable
        && event.availableChannels.some((available) => available !== 'bell')
        && !topicControls(event, 'mail', configFor(event), defaultFrequency)).length
      : 0;
    const reason = state.editable === 0
      ? `No ${name.toLowerCase()} setting to change in this topic.`
      : [
        state.checked === 'indeterminate' ? 'On for some events in this topic.' : undefined,
        narrowed ? `Changes the ${plural(state.editable, 'event')} shown.` : undefined,
        required > 0 ? 'Required events stay on.' : undefined,
        silent > 0 ? `${plural(silent, 'event')} set to Off stay off.` : undefined,
        hint,
      ].filter(Boolean).join(' ');
    return (
      <span className="flex justify-center" title={reason || undefined}>
        <Checkbox
          checked={state.checked}
          disabled={!canConfigure || state.editable === 0}
          aria-label={`${label} ${name}`}
          className={channel === 'mail' && !emailsEnabled ? 'opacity-50' : undefined}
          onCheckedChange={(checked) => onSetTopicChannel(events, channel, checked === true)}
        />
      </span>
    );
  };

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="border-t">
      <div className={`${TOPIC_GRID} py-1.5 text-sm`}>
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium">{label}</span>
          <span className="shrink-0 text-muted-foreground">
            {narrowed ? `${events.length} of ${plural(total, 'event')}` : plural(total, 'event')}
          </span>
          {required > 0 && (
            <Badge variant="outline" className="shrink-0 gap-1">
              <Lock className="h-3 w-3" />
              {required} required
            </Badge>
          )}
          {unsaved > 0 && <Badge variant="secondary" className="shrink-0">{unsaved} unsaved</Badge>}
          {customised > 0 && (
            <Badge variant="outline" className="shrink-0">{customised} customised</Badge>
          )}
        </div>
        <span className="whitespace-nowrap text-muted-foreground" title={REACH_HINTS[reach]}>
          {REACH_LABELS[reach]}
        </span>
        {box('bell', 'Bell')}
        {box('mail', 'Email', digestDelivery === 'digest' ? ONE_DAILY_SENTENCE : undefined)}
        <span className="text-muted-foreground">
          {live.length ? `${onCount} of ${live.length} on` : 'All retired'}
        </span>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 justify-end gap-1 px-2"
            aria-label={`${open ? 'Hide' : 'Customise'} ${label}`}
          >
            {open ? 'Hide' : 'Customise'}
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="overflow-x-auto border-t bg-muted/20">
          <TooltipProvider delayDuration={200}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-9 min-w-[220px]">Event</TableHead>
                  <TableHead className="h-9 w-[96px]">Reaches</TableHead>
                  <TableHead className="h-9 w-[70px] text-center">On</TableHead>
                  {SETUP_CHANNELS.map((channel) => (
                    <TableHead key={channel} className="h-9 w-[70px] px-2 text-center">
                      {CHANNEL_LABELS[channel]}
                    </TableHead>
                  ))}
                  <TableHead className="h-9 w-[250px]">Frequency</TableHead>
                  <TableHead className="h-9 w-[76px] text-right">Reset</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <NotificationEventRow
                    key={event.key}
                    event={event}
                    config={configFor(event)}
                    dirty={isDirty(event)}
                    canConfigure={canConfigure}
                    frequencyChoices={frequencyChoices}
                    defaultFrequency={defaultFrequency}
                    emailsEnabled={emailsEnabled}
                    bellEnabled={bellEnabled}
                    digestEnabled={digestEnabled}
                    digestDelivery={digestDelivery}
                    onUpdate={onUpdate}
                    onToggleChannel={onToggleChannel}
                    onReset={onReset}
                  />
                ))}
              </TableBody>
            </Table>
          </TooltipProvider>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
